import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MailCheck, ChevronRight, ChevronDown, Share2, Monitor, Network,
  Building2, Radio, ShieldOff, History, Crosshair, UserRound, Globe,
} from 'lucide-react';
import { countryTag } from '../utils/country';
import CustomerIpBadge from '../components/CustomerIpBadge';
import StatusSelect from '../components/StatusSelect';
import EntityGraph, { GraphNode, GraphEdge } from '../components/EntityGraph';
import MitreBar, { MitreTactic } from '../components/incident/MitreBar';
import IncidentTimeline, { TimelineAlert } from '../components/incident/IncidentTimeline';
import MdrServiceStatus from '../components/incident/MdrServiceStatus';
import EntityFixing from '../components/incident/EntityFixing';
import SoarExecutionHistory from '../components/incident/SoarExecutionHistory';
import AssetHoverLabel from '../components/AssetHoverLabel';

interface CaseDetail {
  id: number;
  tenant_id: number;
  tenant_name?: string;
  tenant_contact_email: string | null;
  title: string;
  status: string;
  severity: string;
  mitre_tactic: string | null;
  mitre_technique: string | null;
  entity_host: string | null;
  entity_host_label: string | null;
  entity_user: string | null;
  asset_id: number | null;
  asset_ip_address: string | null;
  asset_mac_address: string | null;
  assigned_user_name: string | null;
  last_reported_at: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

type Alert = TimelineAlert & { host: string | null; entity_user: string | null; src_ip: string | null; src_country: string | null; customer_ip: { cidr: string; label: string | null; siteName: string | null } | null };

interface History {
  previous_cases: number;
  last_7_days: number;
  last_seen_at: string | null;
  // Case precedenti sulla stessa identità (qualunque host), asse identità 2026-09-18.
  user_previous_cases: number;
}

interface Note {
  id: number;
  note: string;
  user_name: string | null;
  created_at: string;
}

interface Report {
  id: number;
  sent_to: string;
  subject: string;
  body: string;
  sent_by_name: string | null;
  sent_at: string;
}

const SEVERITY_BAR: Record<string, string> = {
  low: 'bg-gray-400', medium: 'bg-amber-500', high: 'bg-orange-500', critical: 'bg-red-600',
};

const SEVERITY_DOT: Record<string, string> = {
  low: 'bg-slate-400', medium: 'bg-amber-400', high: 'bg-orange-400', critical: 'bg-red-500',
};

const TABS = ['Attack Chain', 'Timeline', 'Entity Fixing', 'MDR Service Status', 'SOAR Execution History'] as const;
type Tab = typeof TABS[number];

// Sintesi discorsiva dell'incidente a partire dai suoi alert: non esiste un campo "description"
// per il case (solo `title`, spesso il nome nudo della regola, es. "Rilevamento malware" — non
// dice cosa sia realmente successo), quindi la componiamo lato client dai messaggi degli alert
// già collegati, che dal lavoro di arricchimento di oggi sono frasi discorsive vere e proprie
// (vedi ingestion/logParser.ts, services/edr/types.ts) invece di semplici stringhe grezze.
// Firma di un messaggio: il testo privato di ciò che cambia ad ogni occorrenza (date, orari,
// conteggi). Serve perché la deduplica esatta sulla stringa NON funziona quando il messaggio
// incorpora la propria finestra temporale: un case LECS consolidato con 371 alert produceva 371
// frasi "distinte" identiche tranne le date, concatenate in un muro di testo illeggibile (caso
// reale osservato il 2026-09-15 sul case dello scan da 192.168.168.176).
function messageSignature(message: string): string {
  return message
    .replace(/\d{1,2}\/\d{1,2}\/\d{2,4},?\s*\d{1,2}:\d{2}(:\d{2})?/g, '§')
    .replace(/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?/g, '§')
    .replace(/\b\d{1,2}:\d{2}(:\d{2})?\b/g, '§')
    .replace(/\(\s*\d+\s+events?\s*\)/gi, '(N events)')
    .replace(/\s+/g, ' ')
    .trim();
}

// Quanti schemi di messaggio diversi mostrare prima di riassumere il resto: oltre questo numero
// l'elenco smette di informare e torna a essere un muro di testo.
const MAX_PATTERNS = 5;
const MAX_PATTERN_CHARS = 240;

interface IncidentDescription {
  intro: string;
  patterns: { text: string; count: number }[];
  hiddenPatterns: number;
}

// Sintesi dell'incidente a partire dai suoi alert: non esiste un campo "description" per il case
// (solo `title`, spesso il nome nudo della regola, es. "Rilevamento malware" — non dice cosa sia
// realmente successo), quindi la componiamo lato client dai messaggi degli alert collegati.
// Occorrenze della stessa attività vengono accorpate per firma e contate, invece di essere
// ripetute: quello che interessa all'analista è *quali* cose sono successe e quante volte, non
// rileggere 371 volte la stessa frase con una data diversa.
function buildIncidentDescription(caseData: CaseDetail, alerts: Alert[]): IncidentDescription {
  if (alerts.length === 0) return { intro: 'No alert linked to this incident yet.', patterns: [], hiddenPatterns: 0 };

  const host = caseData.entity_host
    ? ` on ${caseData.entity_host}${caseData.entity_user ? ` (user ${caseData.entity_user})` : ''}`
    : caseData.entity_user ? ` on the identity ${caseData.entity_user}` : '';
  const ruleNames = [...new Set(alerts.map(a => a.rule_name).filter(Boolean))];
  const opened = new Date(caseData.created_at).toLocaleString('en-GB');

  // Periodo coperto dagli alert: sostituisce la lettura di centinaia di timestamp nel testo.
  const times = alerts.map(a => new Date(a.created_at).getTime()).filter(t => !Number.isNaN(t)).sort((a, b) => a - b);
  const first = times.length ? new Date(times[0]).toLocaleString('en-GB') : '';
  const last = times.length ? new Date(times[times.length - 1]).toLocaleString('en-GB') : '';
  // Confronto sulle stringhe già formattate, non sui millisecondi: alert distanti pochi secondi
  // mostrerebbero altrimenti un intervallo fra due istanti identici a video ("X → X").
  const span = first && last && first !== last ? ` Alerts span ${first} → ${last}.` : '';

  const intro = alerts.length === 1
    ? `This incident${host} was opened on ${opened} following a single alert.`
    : `This incident${host} was opened on ${opened} and groups ${alerts.length} alerts`
      + (ruleNames.length > 1 ? ` across ${ruleNames.length} detection rules` : '') + '.' + span;

  // Raggruppamento per firma, conservando come testo mostrato il primo messaggio incontrato
  // (quello reale, con le sue date: serve un esempio concreto, non la firma normalizzata).
  const groups = new Map<string, { text: string; count: number }>();
  for (const alert of alerts) {
    if (!alert.message) continue;
    const key = messageSignature(alert.message);
    const existing = groups.get(key);
    if (existing) existing.count += 1;
    else groups.set(key, { text: alert.message, count: 1 });
  }

  const ordered = [...groups.values()].sort((a, b) => b.count - a.count);
  const patterns = ordered.slice(0, MAX_PATTERNS).map(p => ({
    ...p,
    text: p.text.length > MAX_PATTERN_CHARS ? `${p.text.slice(0, MAX_PATTERN_CHARS).trimEnd()}…` : p.text,
  }));
  return { intro, patterns, hiddenPatterns: Math.max(0, ordered.length - MAX_PATTERNS) };
}

// Intervallo fra due alert consecutivi, mostrato fra un passo e l'altro della timeline.
function formatGap(from: string, to: string): string {
  const seconds = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000);
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState<CaseDetail | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [graph, setGraph] = useState<{ nodes: GraphNode[]; edges: GraphEdge[] }>({ nodes: [], edges: [] });
  const [mitre, setMitre] = useState<MitreTactic[]>([]);
  const [history, setHistory] = useState<History | null>(null);
  const [tab, setTab] = useState<Tab>('Attack Chain');
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const res = await axios.get(`/api/cases/${caseId}`);
    setCaseData(res.data.data.case);
    setAlerts(res.data.data.alerts);
    setNotes(res.data.data.notes);
    setReports(res.data.data.reports);
    setGraph(res.data.data.graph);
    setMitre(res.data.data.mitreCoverage);
    setHistory(res.data.data.history);
  }

  useEffect(() => { load(); }, [caseId]);

  async function handleStatusChange(status: string) {
    setError(null);
    try {
      await axios.put(`/api/cases/${caseId}`, { status });
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while updating the status');
    }
  }

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!newNote.trim()) return;
    setError(null);
    try {
      await axios.post(`/api/cases/${caseId}/notes`, { note: newNote });
      setNewNote('');
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error while adding the note");
    }
  }

  if (!caseData) return <div className="p-4 text-gray-400">Loading…</div>;

  return (
    <div>
      <Link to="/incidents" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={16} /> Back to Security Incidents
      </Link>
      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      <div className="card overflow-hidden mb-4">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`w-1.5 h-6 rounded-sm ${SEVERITY_BAR[caseData.severity]}`} />
            <h1 className="text-lg font-semibold text-gray-900">{caseData.title}</h1>
            <StatusSelect value={caseData.status} onChange={handleStatusChange} />
            {caseData.last_reported_at && (
              <span className="inline-flex items-center gap-1 badge bg-emerald-100 text-emerald-700"
                title={`Reported on ${new Date(caseData.last_reported_at).toLocaleString('en-GB')}`}>
                <MailCheck size={12} /> Reported
              </span>
            )}
            {/* Azioni non ancora disponibili: il motivo è scritto accanto ai pulsanti, non
                nascosto in un tooltip da scoprire col mouse. Un pulsante grigio e muto sembra
                guasto o un permesso mancante, e induce a cercare un problema che non esiste —
                stessa scelta già fatta per i suggerimenti SOAR nella coda di SOC Operations. */}
            <div className="ml-auto flex items-center gap-2">
              <span className="badge bg-gray-100 text-gray-500">In arrivo con il motore SOAR (Fase 3)</span>
              <button disabled title="Richiede il motore di risposta SOAR, non ancora sviluppato"
                className="btn-secondary opacity-40 cursor-not-allowed flex items-center gap-1">
                <ShieldOff size={14} /> Fix <ChevronDown size={12} />
              </button>
              <button disabled title="La gestione delle whitelist non è ancora sviluppata"
                className="btn-secondary opacity-40 cursor-not-allowed">Add to Whitelist</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-3 text-sm">
            <div>
              <span className="text-gray-400 text-xs block">Source</span>
              <span className="text-gray-800 flex items-center gap-1">
                <Radio size={13} className="text-gray-400" />
                {[...new Set(alerts.map(a => a.parsed?.cef
                  ? `${a.parsed.cef.deviceVendor} ${a.parsed.cef.deviceProduct}`
                  : a.source || 'syslog'))].join(', ') || '—'}
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Affected Assets</span>
              <span className="flex items-center gap-3 text-gray-800">
                <span className="flex items-center gap-1"><Monitor size={13} className="text-gray-400" />
                  {caseData.entity_host
                    ? <AssetHoverLabel hostname={caseData.entity_host} label={caseData.entity_host_label} ipAddress={caseData.asset_ip_address} macAddress={caseData.asset_mac_address} assetId={caseData.asset_id} />
                    : '—'}
                </span>
                {caseData.asset_ip_address && (
                  <span className="flex items-center gap-1"><Network size={13} className="text-gray-400" />{caseData.asset_ip_address}</span>
                )}
                <span className="flex items-center gap-1"><Building2 size={13} className="text-gray-400" />{caseData.tenant_name}</span>
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Affected Identity &amp; Origin</span>
              <span className="flex items-center gap-3 text-gray-800">
                <span className="flex items-center gap-1"><UserRound size={13} className="text-violet-500" />{caseData.entity_user || '—'}</span>
                {(() => {
                  // IP del cliente: una sola evidenza per IP, con l'etichetta del blocco.
                  const customer = [...new Map(alerts.filter(a => a.customer_ip && a.src_ip).map(a => [a.src_ip!, a.customer_ip!])).entries()];
                  return customer.length > 0 && (
                    <span className="flex items-center gap-1">
                      {customer.map(([ip, block]) => <span key={ip} className="inline-flex items-center gap-1"><span className="font-mono text-xs">{ip}</span><CustomerIpBadge label={block.label} siteName={block.siteName} /></span>)}
                    </span>
                  );
                })()}
                {(() => {
                  const countries = [...new Set(alerts.map(a => a.src_country).filter((c): c is string => !!c && c !== 'LAN'))];
                  return countries.length > 0 && (
                    <span className="flex items-center gap-1"><Globe size={13} className="text-gray-400" />
                      {countries.map(cc => { const t = countryTag(cc); return t ? <span key={cc} className="badge bg-gray-100 text-gray-700" title={t.title}>{t.text}</span> : null; })}
                    </span>
                  );
                })()}
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Agent Status</span>
              <span className="text-gray-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300" /> Not installed
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xs block mb-0.5">MITRE ATT&CK</span>
              <MitreBar tactics={mitre} />
            </div>
            <div>
              <span className="text-gray-400 text-xs block">Opened</span>
              <span className="text-gray-800">{new Date(caseData.created_at).toLocaleString('en-GB')}</span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-gray-400 text-xs block mb-1">Description</span>
            {(() => {
              const { intro, patterns, hiddenPatterns } = buildIncidentDescription(caseData, alerts);
              return (
                <>
                  <p className="text-sm text-gray-700 leading-relaxed">{intro}</p>
                  {patterns.length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {patterns.map(p => (
                        <li key={p.text} className="flex items-start gap-2 text-sm text-gray-600">
                          {/* Il conteggio sostituisce la ripetizione: "×371" invece di 371 frasi. */}
                          <span className="badge bg-gray-100 text-gray-600 shrink-0 mt-0.5 tabular-nums">
                            ×{p.count}
                          </span>
                          <span className="leading-relaxed">{p.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {hiddenPatterns > 0 && (
                    <p className="text-xs text-gray-400 mt-1.5">
                      +{hiddenPatterns} altre varianti non mostrate — l'elenco completo è nella scheda Timeline.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        <div className="flex gap-6 px-5 border-b border-gray-100 overflow-x-auto">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t ? 'border-primary-600 text-primary-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'Attack Chain' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3">
              <div className="lg:col-span-2 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 size={15} className="text-primary-600" />
                  <span className="text-sm font-medium text-gray-800">Entities involved</span>
                </div>
                <EntityGraph nodes={graph.nodes} edges={graph.edges} />
              </div>

              <div className="border-t lg:border-t-0 lg:border-l border-gray-100 bg-slate-900 text-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">{alerts.length} {alerts.length === 1 ? 'Alert' : 'Alerts'}</span>
                  <span className="text-xs text-slate-400">oldest first</span>
                </div>
                <ol className="space-y-0">
                  {alerts.map((a, i) => (
                    <li key={a.id}>
                      <Link to={`/alerts/${a.id}`}
                        className="group flex items-start gap-3 rounded-md px-2 py-2 -mx-2 hover:bg-slate-800 transition-colors">
                        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${SEVERITY_DOT[caseData.severity]}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-slate-100 truncate">{a.rule_name || 'Unclassified alert'}</span>
                          <span className="block text-xs text-slate-400 truncate">{a.message}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            {new Date(a.created_at).toLocaleString('en-GB')}
                          </span>
                        </span>
                        <ChevronRight size={14} className="mt-1 text-slate-600 group-hover:text-slate-300" />
                      </Link>
                      {i < alerts.length - 1 && (
                        <div className="flex items-center gap-2 pl-1 py-1">
                          <span className="w-px h-5 bg-slate-700 ml-0.5" />
                          <span className="text-[11px] text-slate-500">
                            {formatGap(alerts[i].created_at, alerts[i + 1].created_at)}
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                  {alerts.length === 0 && <li className="text-slate-500 text-sm">No alert linked yet</li>}
                </ol>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border-t border-gray-100">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <History size={15} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-800">Historical attacks</span>
                </div>
                <p className="text-sm text-gray-600">
                  {history?.previous_cases
                    ? <>This asset already had <span className="font-medium text-gray-900">{history.previous_cases}</span> other
                      incident(s){history.last_7_days ? <>, of which <span className="font-medium text-gray-900">{history.last_7_days}</span> in the last 7 days</> : null}.
                      {history.last_seen_at && <> Last one on {new Date(history.last_seen_at).toLocaleString('en-GB')}.</>}</>
                    : caseData.entity_host ? 'No previous incident recorded on this asset.' : 'No host involved in this incident.'}
                  {caseData.entity_user && (
                    <> {history?.user_previous_cases
                      ? <>The identity <span className="font-medium text-gray-900">{caseData.entity_user}</span> appears in <span className="font-medium text-gray-900">{history.user_previous_cases}</span> other incident(s), on any host.</>
                      : <>No other incident involves the identity <span className="font-medium text-gray-900">{caseData.entity_user}</span>.</>}</>
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crosshair size={15} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-800">Attack surface</span>
                </div>
                <p className="text-sm text-gray-600">
                  {graph.nodes.filter(n => n.external).length} external entit(y/ies) and{' '}
                  {graph.nodes.filter(n => !n.external && n.type !== 'host').length} internal entit(y/ies) observed
                  across {alerts.length} alert(s).
                </p>
              </div>
            </div>
          </>
        )}

        {tab === 'Timeline' && <IncidentTimeline alerts={alerts} />}
        {tab === 'Entity Fixing' && <EntityFixing nodes={graph.nodes} />}
        {tab === 'MDR Service Status' && <MdrServiceStatus caseData={caseData} notes={notes} reports={reports} />}
        {tab === 'SOAR Execution History' && <SoarExecutionHistory />}
      </div>

      <div className="card p-4 mb-4">
        <h2 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <MailCheck size={16} className="text-emerald-600" /> Customer reports ({reports.length})
        </h2>
        <ul className="space-y-3">
          {reports.map(r => (
            <li key={r.id} className="border-l-2 border-emerald-300 pl-3 text-sm">
              <div className="text-gray-500 text-xs">
                {new Date(r.sent_at).toLocaleString('en-GB')} · sent to {r.sent_to} by {r.sent_by_name || 'User'}
              </div>
              <div className="font-medium text-gray-800">{r.subject}</div>
              <pre className="mt-1 bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs text-gray-700 whitespace-pre-wrap break-words">{r.body}</pre>
            </li>
          ))}
          {reports.length === 0 && <li className="text-gray-400 text-sm">No report sent to the customer yet</li>}
        </ul>
      </div>

      <div className="card p-4">
        <h2 className="font-medium text-gray-900 mb-3">Notes</h2>
        <ul className="space-y-2 mb-3">
          {notes.map(n => (
            <li key={n.id} className="text-sm">
              <span className="text-gray-500 text-xs">{new Date(n.created_at).toLocaleString('en-GB')} · {n.user_name || 'User'}</span>
              <p className="text-gray-800">{n.note}</p>
            </li>
          ))}
          {notes.length === 0 && <li className="text-gray-400 text-sm">No note added yet</li>}
        </ul>
        <form onSubmit={handleAddNote} className="flex gap-2">
          <input
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Add a note…"
            className="input-field flex-1"
          />
          <button type="submit" className="btn-primary">Add</button>
        </form>
      </div>
    </div>
  );
}
